<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CardsKanbanItem from 'custom/dashboard/routes/dashboard/cards/components/CardsKanbanItem.vue';
import CardsFilterPopover from 'custom/dashboard/routes/dashboard/cards/components/CardsFilterPopover.vue';
import CardShowModal from 'custom/dashboard/routes/dashboard/cards/components/CardShowModal.vue';
import CardNewDialog from 'custom/dashboard/routes/dashboard/cards/components/CardNewDialog.vue';
import {
  DEFAULT_FILTERS,
  countActiveFilters,
  matchesFilters,
} from 'custom/dashboard/routes/dashboard/cards/cardFilters';
import { useCardTaskTypes } from 'custom/dashboard/routes/dashboard/cards/useCardTaskTypes';
import { useConversationCards } from 'custom/dashboard/composables/zimobi/useConversationCards';
import { useConversationSidebarTab } from 'custom/dashboard/composables/zimobi/useConversationSidebarTab';

const props = defineProps({
  // The conversation's display id.
  conversationId: { type: Number, required: true },
  contactName: { type: String, default: '' },
});
const emit = defineEmits(['modalToggle']);

const { t } = useI18n();
const currentUserId = useMapGetter('getCurrentUserID');
const currentChat = useMapGetter('getSelectedChat');
const { cards, isLoaded, fetchCards } = useConversationCards(
  () => props.conversationId
);
const { taskTypes, fetchTaskTypes } = useCardTaskTypes();
const { closeSidebar } = useConversationSidebarTab();

const filters = ref({ ...DEFAULT_FILTERS });
const showFilters = ref(false);
const openCardId = ref(null);
const isCardChanged = ref(false);
const newCardDialog = ref(null);
const isNewCardOpen = ref(false);

watch(
  () => !!openCardId.value || isNewCardOpen.value,
  isOpen => emit('modalToggle', isOpen)
);

const activeFilterCount = computed(() => countActiveFilters(filters.value));
const visibleCards = computed(() =>
  cards.value.filter(card =>
    matchesFilters(card, filters.value, currentUserId.value)
  )
);
const sections = computed(() =>
  [
    {
      key: 'open',
      label: t('CARDS.CONVERSATION_PANEL.OPEN'),
      cards: visibleCards.value.filter(card => card.status === 'open'),
    },
    {
      key: 'closed',
      label: t('CARDS.CONVERSATION_PANEL.CLOSED'),
      cards: visibleCards.value.filter(card => card.status !== 'open'),
    },
  ].filter(section => section.cards.length)
);

const closeCard = () => {
  openCardId.value = null;
  if (isCardChanged.value) fetchCards();
  isCardChanged.value = false;
};

const openNewCard = () => {
  isNewCardOpen.value = true;
  newCardDialog.value.open();
};

const onCardCreated = card => {
  fetchCards();
  openCardId.value = card.id;
};

onMounted(() => {
  fetchCards();
  fetchTaskTypes();
});
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <header
      class="relative flex items-center gap-2 px-4 py-3 border-b border-n-weak"
    >
      <div class="flex flex-col min-w-0">
        <h2 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CARDS.CONVERSATION_PANEL.TITLE') }}
        </h2>
        <span v-if="contactName" class="text-sm truncate text-n-slate-11">
          {{ contactName }}
        </span>
      </div>
      <div
        v-on-clickaway="() => (showFilters = false)"
        class="ltr:ml-auto rtl:mr-auto"
      >
        <div class="relative">
          <Button
            v-tooltip.bottom="$t('CARDS.FILTERS.BUTTON')"
            icon="i-lucide-list-filter"
            color="slate"
            size="sm"
            variant="ghost"
            data-testid="conversation-cards-filter-button"
            :class="{ 'bg-n-alpha-2': showFilters }"
            @click="showFilters = !showFilters"
          />
          <span
            v-if="activeFilterCount"
            class="absolute -top-1 ltr:-right-1 rtl:-left-1 grid h-4 px-1 text-[10px] font-semibold text-white rounded-full min-w-4 place-content-center bg-n-brand pointer-events-none"
            data-testid="conversation-cards-filter-count"
          >
            {{ activeFilterCount }}
          </span>
        </div>
        <!-- Anchored to the header, not the button, so it stays inside the narrow sidebar. -->
        <CardsFilterPopover
          v-if="showFilters"
          :filters="filters"
          :task-types="taskTypes"
          :stages="[]"
          :show-status="false"
          @update="value => (filters = value)"
        />
      </div>
      <Button
        v-tooltip.bottom="$t('CARDS.NEW_CARD.BUTTON')"
        icon="i-lucide-plus"
        color="slate"
        size="sm"
        variant="ghost"
        data-testid="conversation-cards-new"
        @click="openNewCard"
      />
      <Button
        icon="i-lucide-x"
        color="slate"
        size="sm"
        variant="ghost"
        data-testid="conversation-cards-close"
        @click="closeSidebar"
      />
    </header>

    <div class="flex flex-col flex-1 gap-5 p-4 overflow-y-auto">
      <Spinner v-if="!isLoaded" class="self-center text-n-brand" />
      <p
        v-else-if="!sections.length"
        class="mb-0 text-sm text-center text-n-slate-11"
        data-testid="conversation-cards-empty"
      >
        {{
          cards.length
            ? $t('CARDS.CONVERSATION_PANEL.NO_MATCHES')
            : $t('CARDS.CONVERSATION_PANEL.EMPTY')
        }}
      </p>
      <section
        v-for="section in sections"
        :key="section.key"
        class="flex flex-col gap-2"
        :data-testid="`conversation-cards-${section.key}`"
      >
        <h3
          class="flex items-center gap-2 mb-0 text-sm font-medium text-n-slate-11"
        >
          {{ section.label }}
          <span
            class="grid h-5 px-1.5 text-xs rounded-md min-w-5 place-content-center bg-n-alpha-2"
          >
            {{ section.cards.length }}
          </span>
        </h3>
        <CardsKanbanItem
          v-for="card in section.cards"
          :key="card.id"
          :card="card"
          @open="openCardId = card.id"
        />
      </section>
    </div>

    <CardShowModal
      v-if="openCardId"
      :key="openCardId"
      :card-id="openCardId"
      @close="closeCard"
      @change="isCardChanged = true"
    />
    <CardNewDialog
      ref="newCardDialog"
      :conversation-id="conversationId"
      :contact="currentChat?.meta?.sender"
      @created="onCardCreated"
      @close="isNewCardOpen = false"
    />
  </div>
</template>
